import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import { FloatCurrencyInput } from 'ui-components/components/float-currency-input';
import { FancyStateIcon } from 'ui-components/components/fancy-state-icon/';
import { noop } from 'ui-components/default-props/noop';

import FormattedCurrencyContainer from '../../../components/formatted-currency/container';
import { CurrencySymbolContainer } from '../../../containers/currency-symbol-container';
import { MessageDescriptorPropType } from '../../../prop-types/intl';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';

import { toFixedNumber } from '../../../services/number-formatting-service';

import style from './cost-review-line-item-purchase.module.less';
import { InsuranceProviders } from '../../../enums/insurance-providers';
import { PhoneVerificationContext } from './../../phone-verification/context';
import PhoneVerificationBanner from './../../phone-verification/banner-message';
import { PHONE_VERIFICATION } from '../../../wallet/utilities/constants';
import messages from './messages';
import { noShipsuranceBillingInfoErrors } from '../../../services/insurance-service';

const autoFundMaxPerDayWarning = 1;
const autoFundWarning = 2;
const stampsProviderId = 2;

const CostReviewLineItemPurchase = ({
  onPurchaseAmountChange = noop,
  errorMessage,
  provider = {},
  minPurchaseAmount = 0,
  minPurchaseAmountCurrencyCode = null,
  userHasPurchasingRole = false,
  userHasBillingInfoSetup = true,
  closeOrderDetailsModal = noop,
  closeModal = noop,
  hasPersonalShipsurance = false,
  costWarnings = [],
  providerErrorMessage = '',
  labelProviderId = '',
  insuranceOrPostagePurchaseError: insuranceOrPostagePurchaseErrorProp = '',
  postageCostCurrencyCode = null,
  readOnlyCostReviewModal = false,
}) => {
  const purchaseAmount =
    provider && provider.purchaseAmount !== undefined ? provider.purchaseAmount : { value: minPurchaseAmount, code: 'USD' };
  const { insuranceProviderId } = provider;

  // HACK SPD-9694: Shipsurance insuranceProvider id backdoor
  const isPersonalShipsurance =
    (insuranceProviderId === InsuranceProviders.Shipsurance.insuranceProviderId || insuranceProviderId === '0') && hasPersonalShipsurance;
  const shouldRenderInput = !isPersonalShipsurance && userHasPurchasingRole;
  const maxPerDay = provider.autoFund ? provider.autoFund.maxPerDay : null;
  const warningMsg =
    costWarnings.length && costWarnings[0].errorCode === autoFundMaxPerDayWarning ? (
      <FormattedMessage {...messages.autoFundMaxPerDayReached} values={{ maxPerDay }} />
    ) : (
      <FormattedMessage {...messages.autoFundWarning} />
    );
  const threshHoldPerDayHit = !!costWarnings.filter((warning) => warning.errorCode === autoFundMaxPerDayWarning).length;
  const isAutoFunded = !!costWarnings.filter((warning) => warning.errorCode === autoFundWarning).length;
  const shouldRenderMinPurchase = shouldRenderInput && (!isAutoFunded || threshHoldPerDayHit);
  const { providerIdRequiringPhoneVerification } = useContext(PhoneVerificationContext);
  const isPhoneVerificationError = insuranceOrPostagePurchaseErrorProp?.includes(PHONE_VERIFICATION);

  const insuranceOrPostagePurchaseError =
    noShipsuranceBillingInfoErrors.find((err) => insuranceOrPostagePurchaseErrorProp?.includes(err)) ?? insuranceOrPostagePurchaseErrorProp;

  const findNearestNumToPurchaseValue = useCallback((minAmountOwed, amount) => {
    const nearestDivider = Math.ceil(minAmountOwed / amount);
    const nearestAmountToAutoPurchase = amount * nearestDivider;
    return nearestAmountToAutoPurchase;
  }, []);

  const amountToAutoFund = useCallback(() => {
    const { subTotal, autoFund, balance } = provider;
    const minAmountOwed = subTotal - balance;
    const nearestToPurchaseValue = findNearestNumToPurchaseValue(minAmountOwed, autoFund.purchaseAmount.value);
    return autoFund.purchaseAmount.value < minAmountOwed ? nearestToPurchaseValue : autoFund.purchaseAmount.value;
  }, [findNearestNumToPurchaseValue, provider]);

  const fundsToAdd = useCallback(() => {
    const amount = amountToAutoFund(provider);
    onPurchaseAmountChange(provider, amount);
  }, [onPurchaseAmountChange, amountToAutoFund, provider]);

  const amount = provider.autoFund ? toFixedNumber(amountToAutoFund(), 2) : null;

  useOnComponentMount(() => {
    const hasWarning = costWarnings && costWarnings[0];
    if (hasWarning && costWarnings[0].errorCode === autoFundWarning && provider.autoFund) {
      fundsToAdd(provider);
      return;
    }
    if (!provider.purchaseAmount) {
      onPurchaseAmountChange(provider, minPurchaseAmount);
    }
  });

  if (readOnlyCostReviewModal) {
    return null;
  }

  return (
    <div className={style.container}>
      <div className={style['error-and-input']}>
        <div className={style['purchase-warning']}>
          {((!isPersonalShipsurance && errorMessage && !isAutoFunded) ||
            (insuranceOrPostagePurchaseError && !isPhoneVerificationError)) && (
            <div className={classnames(style.notification, style['notification-error'])}>
              <div
                className={classnames(style['error-text'], {
                  [style['requires-phone-verification']]: providerIdRequiringPhoneVerification,
                })}
              >
                {!providerIdRequiringPhoneVerification &&
                  ((insuranceOrPostagePurchaseError && !isPhoneVerificationError) || <FormattedMessage {...errorMessage} />)}
                {providerIdRequiringPhoneVerification && (
                  <PhoneVerificationBanner sellerLabelProviderId={providerIdRequiringPhoneVerification} />
                )}
              </div>
            </div>
          )}
          {!userHasBillingInfoSetup && !errorMessage && (
            <div className={classnames(style.notification, style['notification-error'])}>
              <div className={style['error-text']}>
                <FormattedMessage
                  defaultMessage='To purchase Shipsurance, please <a>Add Billing Information</a>.'
                  description='shipsuranceAccountFunding.noBillingInformation'
                  id='Ha7YcD'
                  values={{
                    a: (chunks) => (
                      <Link
                        onClick={() => {
                          closeOrderDetailsModal();
                          closeModal();
                        }}
                        to='/settings/payments'
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </div>
            </div>
          )}
        </div>
        {shouldRenderInput && (
          <div className={style['purchase-input']}>
            <FloatCurrencyInput
              className={style['form-input']}
              currencySymbol={<CurrencySymbolContainer currencyCode={postageCostCurrencyCode} />}
              disabled={isAutoFunded}
              label={<FormattedMessage {...messages.costReviewLineItemPurchase} />}
              onChange={(stringValue) => {
                let parsedString = stringValue;
                if (stringValue.indexOf('e') !== -1) {
                  parsedString = stringValue.replace(new RegExp('e', 'g'), '');
                }
                const parsedValue = Number.parseFloat(parsedString);
                if (parsedString === `${parsedValue}`) {
                  onPurchaseAmountChange(provider, parsedValue);
                  return;
                }
                onPurchaseAmountChange(provider, parsedString);
              }}
              value={isAutoFunded ? amount : purchaseAmount.value}
            />
          </div>
        )}
      </div>
      {shouldRenderMinPurchase && (
        <div className={style['min-purchase-message']}>
          <FormattedMessage
            defaultMessage='Minimum purchase {minAmount}'
            description='costReviewLineItemPurchase.neededForPurchase'
            id='G5i0wQ'
            values={{
              minAmount: <FormattedCurrencyContainer currencyCode={minPurchaseAmountCurrencyCode} value={minPurchaseAmount} />,
            }}
          />
        </div>
      )}
      {costWarnings.length > 0 && (
        <div className={style['provider-error-message']}>
          <div className={style.warning}>
            <div className={style['warning-text']}>{warningMsg}</div>
          </div>
        </div>
      )}
      {providerErrorMessage && (
        <div>
          {parseInt(labelProviderId, 10) === stampsProviderId ? (
            <div className={style['provider-error-message']}>
              <div className={classnames(style.notification, style['notification-error'])}>
                <div className={style['error-icon']}>
                  <FancyStateIcon state='error' />
                </div>
                <div className={style['error-text']}>
                  <FormattedMessage
                    defaultMessage='There is an issue with your Stamps.com account. Please contact their support to resolve the issue and continue purchasing postage. Stamps.com Support ({stampsErrorMsg}) Support: +1-855-889-7867.'
                    description='costReviewLineItemPurchase.stampsIssueError'
                    id='ogt3vJ'
                    values={{
                      stampsErrorMsg: (
                        <a href='https://stamps.custhelp.com/' rel='noopener noreferrer' target='_blank'>
                          link
                        </a>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div
              className={style['error-text']}
              // oxlint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: providerErrorMessage }}
            />
          )}
        </div>
      )}
    </div>
  );
};

CostReviewLineItemPurchase.propTypes = {
  onPurchaseAmountChange: PropTypes.func,
  errorMessage: MessageDescriptorPropType,
  provider: PropTypes.object,
  minPurchaseAmount: PropTypes.number,
  minPurchaseAmountCurrencyCode: PropTypes.string,
  userHasPurchasingRole: PropTypes.bool,
  userHasBillingInfoSetup: PropTypes.bool,
  closeOrderDetailsModal: PropTypes.func,
  closeModal: PropTypes.func,
  hasPersonalShipsurance: PropTypes.bool,
  costWarnings: PropTypes.array,
  providerErrorMessage: PropTypes.string,
  labelProviderId: PropTypes.string,
  postageCostCurrencyCode: PropTypes.string,
  insuranceOrPostagePurchaseError: PropTypes.string,
  readOnlyCostReviewModal: PropTypes.bool,
};

export default CostReviewLineItemPurchase;
